import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, BookMarked, Search, X, Loader2, Sparkles } from "lucide-react";
import { api, euro, num } from "../../lib/api";
import { displayPricebookCode } from "../../lib/pricebookCodes";

const EMPTY = { wbs: "", category: "", subcategory: "", code: "", description: "", unit: "", quantity: 1, unit_price: 0, labor_pct: 0 };

// La gerarchia dell'offerta è distinta da quella della fonte prezzario.
const cmeGroupKey = (row) => JSON.stringify([
  (row.wbs || "").trim() || "WBS da assegnare",
  (row.category || "").trim() || "Senza categoria",
  (row.subcategory || "").trim(),
]);
const cmeGroupTitle = (key) => JSON.parse(key).filter(Boolean).join(" › ");

function PrezzarioPicker({ offerId, region, offerProvince = "", existingItems = [], onClose, onAdded }) {
  const [catalog, setCatalog] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(region || "");
  const [province, setProvince] = useState(offerProvince || "");
  const [sourceTree, setSourceTree] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState("");
  const [wbs, setWbs] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const pageSize = 50;
  const sourceRegion = catalog.find(row => row.region === selectedRegion);
  const canSearch = selectedRegion && (selectedRegion !== "Toscana" || province);
  const wbsValues = useMemo(
    () => [...new Set(existingItems.map(it => (it.wbs || "").trim()).filter(Boolean))].sort(),
    [existingItems]
  );
  const categoryValues = useMemo(
    () => [...new Set(existingItems.filter(it => (it.wbs || "").trim() === wbs)
      .map(it => (it.category || "").trim()).filter(Boolean))].sort(),
    [existingItems, wbs]
  );
  const subcategoryValues = useMemo(
    () => [...new Set(existingItems.filter(it => (it.wbs || "").trim() === wbs && (it.category || "").trim() === category)
      .map(it => (it.subcategory || "").trim()).filter(Boolean))].sort(),
    [existingItems, wbs, category]
  );
  const sourceOptions = useMemo(() => {
    const options = [];
    for (const macro of sourceTree) {
      options.push({ label: macro.name, query: { macro: macro.name } });
      for (const cat of macro.categories || []) {
        options.push({ label: `  ↳ ${cat.name}`, query: { macro: macro.name, category: cat.name } });
        for (const sub of cat.subcategories || []) {
          options.push({ label: `    ↳ ${sub.name}`, query: { macro: macro.name, category: cat.name, subcategory: sub.name } });
        }
      }
    }
    return options;
  }, [sourceTree]);

  useEffect(() => {
    let active = true;
    api.prezzariCatalog().then(data => {
      if (active) setCatalog(data.regions || []);
    }).catch(() => { if (active) toast.error("Impossibile caricare le fonti dei prezzari"); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setSourceTree([]);
    setSourceFilter("");
    setPage(0);
    if (canSearch) api.prezzariTree(selectedRegion, province || undefined)
      .then(tree => { if (active) setSourceTree(tree || []); })
      .catch(() => { if (active) toast.error("Errore nella classificazione del prezzario"); });
    return () => { active = false; };
  }, [selectedRegion, province, canSearch]);

  useEffect(() => {
    let active = true;
    if (!canSearch) { setItems([]); setTotal(0); return () => { active = false; }; }
    const params = sourceFilter ? JSON.parse(sourceFilter) : {};
    setLoading(true);
    api.prezzariPage({ region: selectedRegion, province: province || undefined,
      ...params, q, page, page_size: pageSize })
      .then(data => {
        if (active) { setItems(data.items || []); setTotal(data.total || 0); }
      })
      .catch(err => {
        if (active) {
          setItems([]); setTotal(0);
          toast.error(err?.response?.data?.detail || "Ricerca prezzario non riuscita");
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedRegion, province, canSearch, sourceFilter, q, page]);

  const chooseWbs = (value) => {
    setWbs(value);
    const cats = [...new Set(existingItems.filter(it => (it.wbs || "").trim() === value)
      .map(it => (it.category || "").trim()).filter(Boolean))];
    if (cats.length === 1) {
      setCategory(cats[0]);
      const subs = [...new Set(existingItems.filter(it => (it.wbs || "").trim() === value && it.category === cats[0])
        .map(it => (it.subcategory || "").trim()).filter(Boolean))];
      setSubcategory(subs.length === 1 ? subs[0] : "");
    } else {
      setCategory("");
      setSubcategory("");
    }
  };

  const add = async (p) => {
    if (!wbs.trim() || !category.trim()) {
      toast.error("Indica la fase WBS e la categoria dell'offerta");
      return;
    }
    if (selectedRegion === "Toscana" && !province) {
      toast.error("Seleziona prima una provincia");
      return;
    }
    setAdding(p.id);
    try {
      await api.addFromPrezzario(offerId, p.id, {
        wbs: wbs.trim(), category: category.trim(), subcategory: subcategory.trim(),
        province: selectedRegion === "Toscana" ? province : "",
      });
      toast.success("Voce inserita nella fase WBS " + wbs.trim());
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile associare la voce alla WBS");
    } finally {
      setAdding("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 sm:p-8" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] flex flex-col fade-up" onClick={event => event.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div>
            <h3 className="font-display font-semibold text-lg flex items-center gap-2"><BookMarked className="w-5 h-5 text-orange-600" /> Prezzario → WBS dell'offerta</h3>
            <p className="text-xs text-slate-500 mt-1">Le categorie del prezzario descrivono la fonte; fase WBS, categoria e sottocategoria sono proprie dell'offerta.</p>
          </div>
          <button onClick={onClose} data-testid="prezzario-picker-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-slate-200 space-y-3 overflow-y-auto shrink-0 max-h-[49vh]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Fase WBS dell'offerta *</label>
              <input value={wbs} onChange={e => chooseWbs(e.target.value)} list="wbs-offer-options" data-testid="prezzario-destination-wbs"
                placeholder="Es. 02 - Opere strutturali" className="w-full border rounded-lg px-3 py-2 text-sm" />
              <datalist id="wbs-offer-options">{wbsValues.map(value => <option key={value} value={value} />)}</datalist>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Categoria dell'offerta *</label>
              <input value={category} onChange={e => { setCategory(e.target.value); setSubcategory(""); }}
                list="category-offer-options" data-testid="prezzario-destination-category"
                placeholder="Es. Calcestruzzi" className="w-full border rounded-lg px-3 py-2 text-sm" />
              <datalist id="category-offer-options">{categoryValues.map(value => <option key={value} value={value} />)}</datalist>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Sottocategoria dell'offerta</label>
              <input value={subcategory} onChange={e => setSubcategory(e.target.value)}
                list="subcategory-offer-options" data-testid="prezzario-destination-subcategory"
                placeholder="Es. Fondazioni" className="w-full border rounded-lg px-3 py-2 text-sm" />
              <datalist id="subcategory-offer-options">{subcategoryValues.map(value => <option key={value} value={value} />)}</datalist>
            </div>
          </div>
          <p className="text-xs text-slate-500">Puoi riutilizzare una WBS già presente nel computo oppure crearne una nuova scrivendone il nome. La classificazione ufficiale resta collegata alla voce, non sostituisce la categoria dell'offerta.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {!region && <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Regione del prezzario</label>
              <select value={selectedRegion} onChange={e => { setSelectedRegion(e.target.value); setProvince(""); setPage(0); setQ(""); }}
                data-testid="prezzario-picker-region" className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Seleziona regione</option>
                {catalog.filter(item => item.imported_count > 0).map(item =>
                  <option key={item.region} value={item.region}>{item.region} · {item.imported_count.toLocaleString("it-IT")} voci</option>
                )}
              </select>
            </div>}
            {selectedRegion === "Toscana" && <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Provincia del prezzario *</label>
              <select value={province} onChange={e => { setProvince(e.target.value); setPage(0); }}
                data-testid="prezzario-picker-province" className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Seleziona provincia</option>
                {(sourceRegion?.provinces || []).map(item => <option key={item.name} value={item.name}>{item.name}</option>)}
              </select>
            </div>}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Classificazione ufficiale del prezzario</label>
              <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(0); }}
                data-testid="prezzario-picker-source-category" className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Tutte le categorie e sottocategorie</option>
                {sourceOptions.map((item, index) => <option key={index} value={JSON.stringify(item.query)}>{item.label}</option>)}
              </select>
            </div>
          </div>
          {selectedRegion === "Lazio" && <p role="status" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Lazio 2023: importazione parziale. Verifica sempre descrizione, codice originale e validità del prezzario per questa gara prima di usare il prezzo.
          </p>}
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => { setQ(e.target.value); setPage(0); }}
              placeholder="Cerca codice o descrizione nel prezzario…" data-testid="prezzario-picker-search"
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 min-h-24">
          {loading ? <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div>
            : !canSearch ? <p className="p-8 text-center text-sm text-slate-500">Seleziona la regione e, per Toscana, la provincia.</p>
            : items.length === 0 ? <p className="p-8 text-center text-sm text-slate-500">Nessuna voce trovata nel filtro scelto.</p>
            : items.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3 border-b border-slate-100 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-orange-600">{displayPricebookCode(p)}</span>
                    <span className="text-[11px] text-slate-400 truncate">{[p.category, p.subcategory].filter(Boolean).join(" › ")}</span>
                  </div>
                  <div className="text-sm text-slate-800 line-clamp-2">{p.description}</div>
                  <div className="text-[11px] text-slate-500">{[p.region, p.year, p.province].filter(Boolean).join(" · ")}{p.source_context_review_required ? " · Contesto da verificare" : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm">{euro(p.price)}</div>
                  <div className="text-[11px] text-slate-400">{p.unit}</div>
                </div>
                <button onClick={() => add(p)} disabled={!wbs.trim() || !category.trim() || !!adding}
                  data-testid={`prezzario-add-voice-cme-button-${p.id}`}
                  className="inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white text-xs font-semibold px-3 py-2 rounded-lg shrink-0">
                  {adding === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Aggiungi
                </button>
              </div>
            ))}
        </div>
        {total > 0 && <div className="p-3 border-t flex items-center justify-between gap-3 text-sm">
          <span className="text-slate-600">{(page * pageSize + 1).toLocaleString("it-IT")}–{Math.min(total, (page + 1) * pageSize).toLocaleString("it-IT")} di {total.toLocaleString("it-IT")} voci</span>
          <div className="flex gap-2">
            <button disabled={page === 0 || loading} onClick={() => setPage(value => Math.max(0, value - 1))}
              className="border rounded-lg px-3 py-1.5 disabled:opacity-40">Precedenti</button>
            <button disabled={(page + 1) * pageSize >= total || loading} onClick={() => setPage(value => value + 1)}
              data-testid="prezzario-picker-next-page" className="border rounded-lg px-3 py-1.5 disabled:opacity-40">Successivi</button>
          </div>
        </div>}
      </div>
    </div>
  );
}

const Cell = ({ value, onSave, type = "text", cls = "", testid }) => {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <input
      value={v}
      type={type}
      data-testid={testid}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (String(v) !== String(value ?? "")) onSave(type === "number" ? parseFloat(v) || 0 : v); }}
      className={`w-full bg-transparent px-2 py-1.5 rounded focus:outline-none focus:bg-orange-50 focus:ring-1 focus:ring-orange-400 ${cls}`}
    />
  );
};

const PriceCandidate = ({ label, row }) => (
  <div className="rounded-lg border border-slate-200 p-3 bg-white">
    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
    {row ? <>
      <div className="mt-1 flex justify-between gap-3"><span className="font-mono text-xs text-slate-700">{row.code || "—"}</span><span className="font-mono text-sm font-semibold">{euro(row.unit_price)}</span></div>
      <div className="text-xs text-slate-700 mt-1">{row.description || "—"}</div>
      <div className="mt-2 text-[10px] text-slate-500">{row.unit || "—"} · {row.match_type || "—"} · {Math.round((row.confidence || 0) * 100)}% · codice {row.code_exact ? "esatto" : "descrittivo"} · U.M. {row.unit_compatible ? "ok" : "non compatibile"}</div>
      <div className="mt-1 text-[10px] text-slate-400">Fonte: {row.source_filename || row.source_key || "—"}{row.year ? ` · ${row.year}` : ""} · {row.authority || "—"}</div>
    </> : <div className="mt-2 text-xs text-slate-400">Nessun candidato affidabile</div>}
  </div>
);

const COMPONENT_EMPTY = { type: "MATERIAL", description: "", unit: "", quantity_per_unit: 1, source_unit_price: 0, source_kind: "", source_ref: "", confidence: 1 };

const isCurrentPriceProposal = (item) => {
  const analysis = item?.price_analysis;
  if (item?.proposed_unit_price == null || !analysis || analysis.status !== "READY" || analysis.applied !== true) return false;
  const proposal = Number(item.proposed_unit_price);
  const analyzed = Number(analysis.proposed_unit_price);
  if (!Number.isFinite(proposal) || !Number.isFinite(analyzed) || Math.abs(proposal - analyzed) > 0.0001) return false;
  if (item.proposal_price_analysis_id && item.price_analysis_id
      && String(item.proposal_price_analysis_id) !== String(item.price_analysis_id)) return false;
  const source = item.proposal_source_key || "";
  const mode = item.proposal_selection_mode || "";
  const analysisSource = analysis.proposed_source_key || analysis.source || "";
  if (analysisSource && analysisSource !== source) return false;
  if (analysis.selection_mode && analysis.selection_mode !== mode) return false;
  const selected = (analysis.candidates || []).find(
    (row) => String(row.candidate_id || "") === String(analysis.selected_candidate_id || "")
  );
  const safeCandidate = selected
    && selected.source_key === source
    && selected.unit_compatible
    && selected.unit_price != null
    && (
      selected.match_type === "DIRECT_CODE"
      || (selected.match_type === "DIRECT_DESCRIPTION" && Number(selected.confidence || 0) >= 0.90)
    );
  if (source === "LAZIO_2023") return !!safeCandidate && (mode === "AUTO_OFFICIAL_HIGH_CONFIDENCE" || mode === "EXPLICIT_HUMAN_SELECTION");
  if (source === "CRATERE_2026") return !!safeCandidate && mode === "EXPLICIT_HUMAN_SELECTION";
  if (source === "ANALISI_PREZZO") {
    const allowed = new Set(["MATERIAL", "LABOR", "EQUIPMENT", "TRANSPORT", "DISPOSAL"]);
    const safeComponents = Array.isArray(analysis.components) && analysis.components.length > 0
      && analysis.components.every((row) => allowed.has(row.type)
        && Number(row.quantity_per_unit) > 0
        && Number(row.source_unit_price) >= 0
        && !!String(row.source_ref || "").trim()
        && !!String(row.source_kind || "").trim()
        && Number(row.confidence) >= 0 && Number(row.confidence) <= 1);
    return mode === "EXPLICIT_COMPONENT_ANALYSIS"
      && analysis.analysis_type === "COMPOSITE_PRICE_ANALYSIS"
      && analysis.source === "ANALISI_PREZZO"
      && safeComponents;
  }
  return false;
};

function CompositePriceEditor({ offerId, item, onDone }) {
  const [rows, setRows] = useState([{ ...COMPONENT_EMPTY }]);
  const [overhead, setOverhead] = useState(0);
  const [profit, setProfit] = useState(0);
  const [busy, setBusy] = useState(false);
  const patch = (i, key, value) => setRows((old) => old.map((row, idx) => idx === i ? { ...row, [key]: value } : row));
  const submit = async (apply) => {
    setBusy(true);
    try {
      const components = rows.map((row) => ({
        ...row,
        quantity_per_unit: Number(row.quantity_per_unit),
        source_unit_price: Number(row.source_unit_price),
        confidence: Number(row.confidence),
      }));
      const result = await api.priceAnalyzeComposite(offerId, item.id, {
        components, overhead_pct: Number(overhead), profit_pct: Number(profit), apply,
      });
      if (result.status === "READY") toast.success(apply ? "Analisi prezzi registrata come proposta" : "Analisi prezzi verificata");
      else toast.info("Analisi prezzi incompleta: verifica fonti e componenti");
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'analisi prezzi");
      await onDone();
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div><div className="font-semibold text-sm text-slate-800">Analisi prezzi per componenti</div><div className="text-[11px] text-slate-500">Solo fonti tracciate: materiali, manodopera, noli, trasporti, smaltimenti.</div></div>
        <button onClick={() => setRows((x) => [...x, { ...COMPONENT_EMPTY }])} className="text-xs font-semibold text-sky-700 hover:underline">+ componente</button>
      </div>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-2 md:grid-cols-9 gap-2 rounded-lg bg-slate-50 p-2">
            <select value={row.type} onChange={(e) => patch(i, "type", e.target.value)} className="border rounded px-2 py-1 text-xs"><option value="MATERIAL">Materiali</option><option value="LABOR">Manodopera</option><option value="EQUIPMENT">Noli</option><option value="TRANSPORT">Trasporti</option><option value="DISPOSAL">Smaltimenti</option></select>
            <input value={row.description} onChange={(e) => patch(i, "description", e.target.value)} placeholder="Descrizione" className="border rounded px-2 py-1 text-xs md:col-span-2" />
            <input value={row.unit} onChange={(e) => patch(i, "unit", e.target.value)} placeholder="U.M. fonte" className="border rounded px-2 py-1 text-xs" />
            <input value={row.quantity_per_unit} onChange={(e) => patch(i, "quantity_per_unit", e.target.value)} type="number" min="0" step="any" placeholder="Q.tà/unità" className="border rounded px-2 py-1 text-xs" />
            <input value={row.source_unit_price} onChange={(e) => patch(i, "source_unit_price", e.target.value)} type="number" min="0" step="any" placeholder="Prezzo fonte" className="border rounded px-2 py-1 text-xs" />
            <input value={row.source_kind} onChange={(e) => patch(i, "source_kind", e.target.value)} placeholder="Tipo fonte" className="border rounded px-2 py-1 text-xs" />
            <input value={row.source_ref} onChange={(e) => patch(i, "source_ref", e.target.value)} placeholder="Rif. fonte" className="border rounded px-2 py-1 text-xs" />
            <div className="flex gap-1"><input value={row.confidence} onChange={(e) => patch(i, "confidence", e.target.value)} type="number" min="0" max="1" step="0.05" title="Confidence 0-1" className="border rounded px-2 py-1 text-xs w-full" />{rows.length > 1 && <button onClick={() => setRows((x) => x.filter((_, idx) => idx !== i))} className="text-red-500 px-1">×</button>}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-[11px] text-slate-500">Spese generali %<input value={overhead} onChange={(e) => setOverhead(e.target.value)} type="number" min="0" className="block mt-1 border rounded px-2 py-1 w-24 text-xs" /></label>
        <label className="text-[11px] text-slate-500">Utile %<input value={profit} onChange={(e) => setProfit(e.target.value)} type="number" min="0" className="block mt-1 border rounded px-2 py-1 w-24 text-xs" /></label>
        <button disabled={busy} onClick={() => submit(false)} className="px-3 py-2 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50">Verifica</button>
        <button disabled={busy} onClick={() => submit(true)} className="px-3 py-2 rounded-lg bg-sky-700 text-white text-xs font-semibold disabled:opacity-50">Registra proposta</button>
      </div>
    </div>
  );
}

export default function CmeTab({ offerId = null, siteId = null, offer, onChange, hideToolbar = false, cleanView = false }) {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [picker, setPicker] = useState(false);
  const [matching, setMatching] = useState(false);
  const [lastMatch, setLastMatch] = useState(null);
  const [priceBusy, setPriceBusy] = useState(null);
  const [priceDetailsId, setPriceDetailsId] = useState(null);
  const [bimVerification, setBimVerification] = useState(null);
  const [expandedEvidenceId, setExpandedEvidenceId] = useState(null);

  const load = async () => {
    const [initialCme, verification] = await Promise.all([
      siteId ? api.getSiteCme(siteId) : api.getCme(offerId),
      siteId ? null : api.takeoffBimCmeVerification(offerId).catch(() => null),
    ]);
    let c = initialCme;
    const pendingAutoApprovals = siteId ? [] : (c.items || []).filter(
      (item) => item.approved_unit_price == null && isCurrentPriceProposal(item)
    );
    if (pendingAutoApprovals.length) {
      await Promise.allSettled(
        pendingAutoApprovals.map((item) => api.priceApprove(offerId, item.id, {}))
      );
      c = await api.getCme(offerId);
    }
    setItems(c.items);
    setTotals(c.totals);
    setBimVerification(verification);
    onChange?.();
  };
  useEffect(() => { load(); }, [offerId, siteId]);

  const markDone = async (it) => {
    try {
      const r = await api.updateCme(it.id, { wbs: it.wbs, category: it.category, subcategory: it.subcategory, code: it.code, description: it.description, unit: it.unit, quantity: it.quantity, unit_price: it.unit_price, labor_pct: it.labor_pct, done: !it.done });
      const n = r.crono_sync?.updated?.length || 0;
      toast.success(!it.done ? (n ? `Voce eseguita: cronoprogramma aggiornato in ${n} cantiere/i` : "Voce segnata eseguita") : "Voce riaperta");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const matchAI = async (force = false) => {
    setMatching(true);
    try {
      const targets = items.filter((it) => it.description && (force || !it.price_analysis));
      let matched = 0;
      let review = 0;
      for (const it of targets) {
        try {
          const result = await api.priceAnalyzeDirect(offerId, it.id, {});
          if (result.applied) matched += 1;
          if (result.status !== "READY") review += 1;
        } catch (_) { review += 1; }
      }
      const summary = { matched, checked: targets.length, review };
      setLastMatch(summary);
      if (targets.length === 0) toast.info("Tutte le righe sono già state analizzate. Usa «Rianalizza tutte» per forzare.");
      else if (matched === 0) toast.info(`Analizzate ${targets.length} righe: nessuna proposta automatica sicura, ${review} da verificare.`);
      else toast.success(`${matched} proposte Lazio 2023 sicure su ${targets.length} righe; ${review} richiedono verifica.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore Price Intelligence");
    } finally {
      setMatching(false);
    }
  };

  const analyzePrice = async (it, selectedCandidateId = null, apply = false) => {
    setPriceDetailsId(it.id);
    setPriceBusy(it.id);
    try {
      const result = await api.priceAnalyzeDirect(offerId, it.id, {
        selected_candidate_id: selectedCandidateId,
        apply,
      });
      if (result.status === "READY") {
        toast.success(result.applied ? "Proposta prezzo registrata" : "Analisi prezzo completata");
      } else {
        toast.info("Analisi prezzo: verifica umana richiesta");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'analisi prezzo");
      await load();
    } finally {
      setPriceBusy(null);
    }
  };

  const approvePrice = async (it) => {
    setPriceBusy(it.id);
    try {
      await api.priceApprove(offerId, it.id, {});
      toast.success("Prezzo proposto approvato senza modificare il prezzo originale");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'approvazione prezzo");
    } finally {
      setPriceBusy(null);
    }
  };

  const openDrawing = async (fileId) => {
    if (!fileId) return;
    const popup = window.open("", "_blank");
    try {
      const blob = await api.fileBlob(fileId);
      const url = URL.createObjectURL(blob);
      if (popup) popup.location = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (popup) popup.close();
      toast.error(e?.response?.data?.detail || "Impossibile aprire la tavola");
    }
  };

  const addRow = async () => { if (siteId) await api.addSiteCme(siteId, EMPTY); else await api.addCme(offerId, EMPTY); load(); };
  const update = async (item, patch) => {
    const merged = { wbs: item.wbs, category: item.category, subcategory: item.subcategory, code: item.code, description: item.description, unit: item.unit, quantity: item.quantity, unit_price: item.unit_price, labor_pct: item.labor_pct, done: !!item.done, ...patch };
    await api.updateCme(item.id, merged);
    load();
  };
  const del = async (id) => {
    const item = items.find((x) => x.id === id);
    const label = item?.code || item?.description || "questa voce";
    if (!window.confirm(`Eliminare definitivamente ${label} dal computo?`)) return;
    await api.deleteCme(id);
    toast.success("Voce eliminata");
    load();
  };
  const verificationByItemId = useMemo(() => {
    const out = {};
    for (const row of bimVerification?.rows || []) {
      for (const id of row.cme_ids || (row.cme_id ? [row.cme_id] : [])) out[id] = row;
    }
    return out;
  }, [bimVerification]);

  if (cleanView) {
    const groups = [];
    const grouped = {};
    items.forEach((it) => {
      const cat = cmeGroupKey(it);
      if (!grouped[cat]) { grouped[cat] = []; groups.push(cat); }
      grouped[cat].push(it);
    });
    let cleanIndex = -1;
    return (
      <div className="space-y-4 fade-up">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                  <th className="px-2 py-2.5 font-semibold w-16">WBS</th>
                  <th className="px-2 py-2.5 font-semibold w-36">Categoria / Sottocategoria</th>
                  <th className="px-2 py-2.5 font-semibold w-24">Codice</th>
                  <th className="px-2 py-2.5 font-semibold">Descrizione</th>
                  <th className="px-2 py-2.5 font-semibold w-16">U.M.</th>
                  <th className="px-2 py-2.5 font-semibold text-right w-24">Q.tà</th>
                  <th className="px-2 py-2.5 font-semibold text-right w-28">P.U. €</th>
                  <th className="px-2 py-2.5 font-semibold text-right w-28">Importo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Computo metrico vuoto.</td></tr>
                ) : groups.map((cat) => {
                  const rows = grouped[cat];
                  const subtotal = rows.reduce((sum, row) => sum + (row.quantity || 0) * (row.unit_price || 0), 0);
                  return <Fragment key={"clean-"+cat}>
                    <tr className="bg-orange-50/70">
                      <td colSpan={7} className="px-3 py-2 font-display font-semibold text-orange-700 text-sm">▸ {cmeGroupTitle(cat)}</td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-orange-700">{num(subtotal)}</td>
                    </tr>
                    {rows.map((it) => {
                      cleanIndex += 1;
                      const idx = cleanIndex;
                      return <tr key={it.id} className="hover:bg-slate-50/60" data-testid={"cme-clean-row-"+it.id}>
                        <td><Cell value={it.wbs} onSave={(v) => update(it,{wbs:v})} cls="font-mono text-xs text-orange-600" /></td>
                        <td className="min-w-[150px]"><div className="space-y-1">
                          <Cell value={it.category} onSave={(v) => update(it,{category:v})} cls="text-xs font-medium" />
                          <div className="border-t border-slate-100 pt-1"><label className="block text-[9px] text-slate-400">Sottocategoria</label>
                            <Cell value={it.subcategory} onSave={(v) => update(it,{subcategory:v})} cls="text-xs" />
                          </div>
                        </div></td>
                        <td><Cell value={it.code} onSave={(v) => update(it,{code:v})} cls="font-mono text-xs" /></td>
                        <td className="align-top"><textarea value={it.description || ""} onChange={(e) => setItems((rows) => rows.map((x) => x.id === it.id ? {...x,description:e.target.value} : x))} onBlur={(e) => update(it,{description:e.target.value})} rows={Math.max(2,Math.ceil((it.description || "").length/100))} className="w-full resize-y bg-transparent px-2 py-1.5 text-sm leading-relaxed text-slate-800 focus:outline-none focus:bg-orange-50 focus:ring-1 focus:ring-orange-400" /></td>
                        <td><Cell value={it.unit} onSave={(v) => update(it,{unit:v})} cls="text-xs" /></td>
                        <td><Cell value={it.quantity} type="number" onSave={(v) => update(it,{quantity:v})} cls="text-right font-mono" testid={"cme-clean-qty-"+idx} /></td>
                        <td><Cell value={it.unit_price} type="number" onSave={(v) => update(it,{unit_price:v})} cls="text-right font-mono" testid={"cme-clean-price-"+idx} /></td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-900">{num((it.quantity || 0)*(it.unit_price || 0))}</td>
                      </tr>;
                    })}
                  </Fragment>;
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-200 bg-slate-50 p-5">
            <div className="ml-auto max-w-xs space-y-1.5 text-sm">
              <Row label="Importo lavori" value={euro(totals.subtotal)} />
              {totals.ribasso_pct > 0 && <Row label={"Ribasso d'asta "+totals.ribasso_pct+"%"} value={"- "+euro(totals.ribasso_amount)} />}
              {totals.ribasso_pct > 0 && <Row label="Importo ribassato" value={euro(totals.discounted)} />}
              <Row label="Oneri sicurezza" value={euro(totals.security_charges)} />
              <div className="flex justify-between pt-2 border-t border-orange-200 text-orange-600 font-display font-bold text-base">
                <span>{siteId ? "TOTALE COMPUTO" : "TOTALE OFFERTA"}</span><span className="font-mono">{euro(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-up">
      {!hideToolbar && <div className="flex flex-wrap gap-2">
        <button onClick={() => setPicker(true)} data-testid="cme-add-from-prezzario-button"
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 active:scale-95 text-white text-sm font-semibold px-4 py-2 rounded-lg">
          <BookMarked className="w-4 h-4" /> Aggiungi da Prezzario
        </button>
        <button onClick={addRow} data-testid="cme-editor-add-row-button"
          className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-semibold px-4 py-2 rounded-lg">
          <Plus className="w-4 h-4" /> Riga vuota
        </button>
        <button onClick={() => matchAI(true)} disabled={matching || items.length === 0} data-testid="cme-match-prezzario-button"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-orange-400" />} Analizza prezzi AI
        </button>
      </div>}
      {lastMatch && lastMatch.checked > 0 && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="cme-match-summary">
          Analizzate {lastMatch.checked} righe: {lastMatch.matched} proposte Lazio 2023 applicabili in sicurezza, {lastMatch.review || 0} in revisione. Il prezzo originale resta invariato.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-2.5 font-semibold w-10" title="Eseguita: aggiorna il cronoprogramma dei cantieri collegati">✓</th>
                <th className="px-2 py-2.5 font-semibold w-16">WBS</th>
                <th className="px-2 py-2.5 font-semibold w-36">Categoria / Sottocategoria</th>
                <th className="px-2 py-2.5 font-semibold w-24">Codice</th>
                <th className="px-2 py-2.5 font-semibold">Descrizione</th>
                <th className="px-2 py-2.5 font-semibold w-16">U.M.</th>
                <th className="px-2 py-2.5 font-semibold text-right w-24">Q.tà</th>
                <th className="px-2 py-2.5 font-semibold text-right w-28">P.U. €</th>
                <th className="px-2 py-2.5 font-semibold text-right w-28">Importo</th>
                <th className="px-2 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">CME vuoto. Aggiungi voci dal prezzario o trasferisci i risultati dell'analisi AI.</td></tr>
              ) : (() => {
                const groups = [];
                const map = {};
                items.forEach((it) => {
                  const cat = cmeGroupKey(it);
                  if (!map[cat]) { map[cat] = []; groups.push(cat); }
                  map[cat].push(it);
                });
                let gi = -1;
                return groups.map((cat) => {
                  const rows = map[cat];
                  const sub = rows.reduce((s, x) => s + (x.quantity || 0) * (x.unit_price || 0), 0);
                  return (
                    <Fragment key={`cat-${cat}`}>
                      <tr className="bg-orange-50/70">
                        <td colSpan={8} className="px-3 py-2 font-display font-semibold text-orange-700 text-sm">▸ {cmeGroupTitle(cat)}</td>
                        <td className="px-2 py-2 text-right font-mono font-semibold text-orange-700">{num(sub)}</td>
                        <td></td>
                      </tr>
                      {rows.map((it) => {
                        gi += 1;
                        const idx = gi;
                        const proposalCurrent = isCurrentPriceProposal(it);
                        const verification = verificationByItemId[it.id];
                        return (
                          <Fragment key={it.id}>
                          <tr data-testid={`cme-row-${it.id}`} className="hover:bg-slate-50/60">
                            <td><Cell value={it.wbs} onSave={(v) => update(it, { wbs: v })} cls="font-mono text-xs text-orange-600" testid={`cme-wbs-${idx}`} /></td>
                            <td className="min-w-[150px]"><div className="space-y-1">
                              <Cell value={it.category} onSave={(v) => update(it, { category: v })} cls="text-xs font-medium" />
                              <div className="border-t border-slate-100 pt-1"><label className="block text-[9px] text-slate-400">Sottocategoria</label>
                                <Cell value={it.subcategory} onSave={(v) => update(it, { subcategory: v })} cls="text-xs" />
                              </div>
                            </div></td>
                            <td className="px-2 text-center"><input type="checkbox" checked={!!it.done} onChange={() => markDone(it)} title={it.done ? `Eseguita il ${(it.done_at || "").slice(0, 10)}` : "Segna come eseguita"} data-testid={`cme-done-${idx}`} className="w-4 h-4 accent-emerald-600" /></td>
                            <td><div className="flex items-center"><Cell value={it.code} onSave={(v) => update(it, { code: v })} cls="font-mono text-xs" />{it.match_confidence > 0 && <span title="Affidabilità abbinamento AI" data-testid={`cme-match-conf-${idx}`} className="text-[10px] font-mono text-orange-600 pr-1">{Math.round(it.match_confidence * 100)}%</span>}</div></td>
                            <td className="align-top"><textarea value={it.description || ""} onChange={(e) => setItems((rows) => rows.map((x) => x.id === it.id ? {...x, description:e.target.value} : x))} onBlur={(e) => update(it, { description: e.target.value })} data-testid={`cme-desc-${idx}`} rows={Math.max(3, Math.ceil((it.description || "").length / 90))} className="w-full resize-y bg-transparent px-2 py-1.5 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap break-words focus:outline-none focus:bg-orange-50 focus:ring-1 focus:ring-orange-400" />{verification && <div className="px-2 pb-1 space-y-0.5"><div className="flex flex-wrap gap-1.5 items-center">{verification.bim_quantity != null && <span className="rounded bg-sky-50 border border-sky-200 px-1.5 py-0.5 text-[9px] font-semibold text-sky-700">BIM {verification.bim_quantity.toLocaleString("it-IT", {maximumFractionDigits:2})} {it.unit}</span>}{verification.pdf_quantity != null && <span className="rounded bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-[9px] font-semibold text-blue-700">PDF {verification.pdf_quantity.toLocaleString("it-IT", {maximumFractionDigits:2})}</span>}{verification.delta_pct != null && <span className={"rounded px-1.5 py-0.5 text-[9px] font-semibold " + (Math.abs(verification.delta_pct) <= 5 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200")}>Δ {verification.delta_pct > 0 ? "+" : ""}{verification.delta_pct}%</span>}<span className="text-[9px] font-semibold text-slate-500">{verification.status}</span></div>{!!verification.bim_sources?.length && <div className="text-[9px] text-sky-700">BIM: {verification.bim_sources.map(x => x.filename).join(" · ")}</div>}{!!verification.drawing_evidence?.length && <div className="text-[9px] text-blue-700">{verification.drawing_evidence.map((x, i) => <div key={i} className="flex flex-wrap items-center gap-1"><span>{x.match_basis === "EXACT_PROJECT_CODE" && x.evidence_type === "PDF_ABACUS_LEGEND" ? "Abaco/legenda ✓" : x.match_basis === "EXACT_PROJECT_CODE" ? "Tavola ✓" : "Tavola candidata"}:</span><button type="button" onClick={() => openDrawing(x.file_id)} className="font-semibold underline decoration-dotted underline-offset-2 hover:text-blue-900">{x.filename}</button>{x.matched_terms?.length ? <span>· {x.matched_terms.join(", ")}</span> : null}</div>)}</div>}{verification.review?.decision && verification.review.decision !== "DA_VERIFICARE" && <div className="text-[9px] text-emerald-700">Verifica: {verification.review.decision}</div>}</div>}</td>
                            <td><Cell value={it.unit} onSave={(v) => update(it, { unit: v })} cls="text-xs" /></td>
                            <td><Cell value={it.quantity} type="number" onSave={(v) => update(it, { quantity: v })} cls="text-right font-mono" testid={`cme-row-quantity-input-${idx}`} /></td>
                            <td className="align-top"><Cell value={it.unit_price} type="number" onSave={(v) => update(it, { unit_price: v })} cls="text-right font-mono" testid={`cme-row-price-input-${idx}`} />{it.legacy_price_candidate != null && <div className="px-2 text-right text-[9px] text-slate-500" title="Valore ereditato da un vecchio arricchimento: non è una proposta Price Intelligence">Legacy {euro(it.legacy_price_candidate)} · solo review</div>}{it.proposed_unit_price != null && <div className="px-2 text-right text-[10px] text-sky-700">Proposta {euro(it.proposed_unit_price)}</div>}{it.proposal_candidate_code && <div className="px-2 text-right text-[9px] text-slate-500">Prezzario {it.proposal_candidate_code}{it.proposal_source_year ? ` · ${it.proposal_source_year}` : ""}{it.proposal_source_filename ? ` · ${it.proposal_source_filename}` : ""}</div>}{it.proposed_unit_price != null && !proposalCurrent && <div className="px-2 text-right text-[9px] font-semibold text-amber-700">DA RIVALIDARE</div>}{it.approved_unit_price != null && <div className="px-2 text-right text-[10px] font-semibold text-emerald-700">Approvato {euro(it.approved_unit_price)}</div>}{it.price_analysis?.status === "REVIEW_REQUIRED" && <div className="px-2 text-right text-[9px] font-semibold text-amber-700">REVISIONE</div>}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-900">{num((it.quantity || 0) * (it.unit_price || 0))}</td>
                            <td className="text-center"><div className="flex flex-col items-center">{verification && <button type="button" onClick={() => setExpandedEvidenceId(expandedEvidenceId === it.id ? null : it.id)} title="Apri dettaglio tecnico BIM/PDF" className="text-[9px] font-semibold text-violet-700 hover:underline">{expandedEvidenceId === it.id ? "Chiudi" : "BIM/PDF"}</button>}<button type="button" onClick={() => analyzePrice(it)} disabled={priceBusy === it.id} title="Analizza prezzi Lazio 2023 / Cratere 2026" className="p-1.5 rounded hover:bg-sky-50 text-sky-600 disabled:opacity-50">{priceBusy === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}</button>{proposalCurrent && <button type="button" onClick={() => approvePrice(it)} disabled={priceBusy === it.id} title="Approva proposta prezzo" className="text-[10px] font-bold text-emerald-700 hover:underline">✓€</button>}{!proposalCurrent && !it.price_analysis?.lazio_2023 && it.price_analysis?.cratere_2026?.candidate_id && <button type="button" onClick={() => analyzePrice(it, it.price_analysis.cratere_2026.candidate_id, true)} disabled={priceBusy === it.id} title="Seleziona esplicitamente benchmark Cratere 2026" className="text-[9px] font-semibold text-slate-500 hover:text-orange-700">C26</button>}{it.price_analysis && <button type="button" onClick={() => setPriceDetailsId(it.id)} title="Apri dettaglio Price Intelligence" className="text-[9px] font-semibold text-sky-700 hover:underline">AI€</button>}<button onClick={() => del(it.id)} data-testid={`cme-delete-${it.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button></div></td>
                          </tr>
                          {priceDetailsId === it.id && it.price_analysis && <tr className="bg-sky-50/40"><td colSpan={10} className="px-4 py-3"><div className="flex items-start justify-between gap-3"><div><div className="text-[10px] uppercase tracking-wide text-sky-700 font-bold">AI€ · Dettaglio prezzo</div><div className="font-semibold text-slate-900">{it.code || "Voce CME"} · {it.description}</div><div className="text-[11px] text-slate-500 mt-1">Originale {euro(it.original_unit_price ?? it.unit_price)}{it.proposed_unit_price != null ? ` · proposta ${euro(it.proposed_unit_price)}` : ""}{it.approved_unit_price != null ? ` · approvato ${euro(it.approved_unit_price)}` : ""}</div></div><button type="button" onClick={() => setPriceDetailsId(null)} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Chiudi</button></div><div className="mt-3 grid md:grid-cols-2 gap-3"><PriceCandidate label="Prezzario Lazio 2023 · fonte ufficiale" row={it.price_analysis.lazio_2023} /><PriceCandidate label="Cratere Centro Italia 2026 · benchmark" row={it.price_analysis.cratere_2026} /></div><div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs"><div className="rounded bg-white border border-slate-200 p-2"><span className="text-slate-400">Quantità</span><div className="font-mono">{it.price_analysis.quantity_verified ? it.price_analysis.verified_quantity : (it.price_analysis.pricing_quantity ?? it.quantity)} {it.unit}</div><div className="text-[10px] text-slate-500">{it.price_analysis.quantity_verified ? "verificata" : "non verificata"} · {it.price_analysis.quantity_source || "—"}</div></div><div className="rounded bg-white border border-slate-200 p-2"><span className="text-slate-400">Comparabilità</span><div className="font-semibold">{it.price_analysis.comparison?.comparability || "—"}</div><div className="text-[10px] text-slate-500">Δ benchmark {it.price_analysis.comparison?.delta_pct != null ? `${it.price_analysis.comparison.delta_pct}%` : "—"}</div></div><div className="rounded bg-white border border-slate-200 p-2"><span className="text-slate-400">Esito</span><div className={it.price_analysis.status === "READY" ? "font-semibold text-emerald-700" : "font-semibold text-amber-700"}>{it.price_analysis.status || "—"}</div><div className="text-[10px] text-slate-500">{(it.price_analysis.flags || []).join(" · ") || "nessun flag"}</div></div></div></td></tr>}
                          {verification && expandedEvidenceId === it.id && <tr className="bg-violet-50/40"><td colSpan={10} className="px-4 py-3"><div className="grid lg:grid-cols-4 gap-3 text-xs"><div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Quantità</div><div className="mt-1">RDO <b>{verification.rdo_quantity?.toLocaleString("it-IT")} {it.unit}</b></div><div>BIM raw <b>{verification.bim_quantity == null ? "—" : verification.bim_quantity.toLocaleString("it-IT", {maximumFractionDigits:3})}</b></div><div>PDF <b>{verification.pdf_quantity == null ? "—" : verification.pdf_quantity.toLocaleString("it-IT", {maximumFractionDigits:3})}</b></div><div>Delta <b>{verification.delta_pct == null ? "—" : `${verification.delta_pct > 0 ? "+" : ""}${verification.delta_pct}%`}</b></div></div><div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">BIM</div><div className="mt-1">{(verification.bim_sources || []).map(x => x.filename).join(" · ") || "Nessuna sorgente BIM"}</div><div className="text-slate-500 mt-1">Livelli: {(verification.levels || []).join(" · ") || "—"}</div><div className="text-slate-500">Famiglie: {(verification.families || []).slice(0,4).join(" · ") || "—"}</div><div className="text-slate-500">Tipi: {(verification.types || []).slice(0,4).join(" · ") || "—"}</div></div><div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Tavole PDF</div><div className="mt-1 space-y-1">{(verification.drawing_evidence || []).length ? verification.drawing_evidence.map((x,i) => <button key={i} type="button" onClick={() => openDrawing(x.file_id)} className="block text-left font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">{x.filename}</button>) : "Nessuna tavola con codice"}</div><div className="text-slate-500 mt-2 space-y-1">{(verification.pdf_quantity_sources || []).length ? verification.pdf_quantity_sources.map((x,i) => <button key={i} type="button" onClick={() => openDrawing(x.file_id)} className="block text-left text-sky-700 underline decoration-dotted underline-offset-2 hover:text-sky-900">{x.filename || "PDF misurato"}</button>) : "Nessuna misura PDF associata"}</div></div><div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Diagnosi</div><div className="mt-1 font-semibold">{verification.status}</div>{(verification.diagnosis || []).map((x,i) => <div key={i} className="mt-1 text-slate-600"><b>{x.code}</b> — {x.text}</div>)}{verification.review?.decision && <div className="mt-2 text-emerald-700">Review: {verification.review.decision}</div>}</div></div>{!!verification.bim_element_samples?.length && <div className="mt-3 overflow-auto rounded-lg border border-slate-200 bg-white"><table className="min-w-full text-[10px]"><thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-2 py-1.5">ID/GUID</th><th className="text-left px-2 py-1.5">Categoria</th><th className="text-left px-2 py-1.5">Famiglia / Tipo</th><th className="text-left px-2 py-1.5">Livello</th><th className="text-left px-2 py-1.5">Origine</th><th className="text-right px-2 py-1.5">Q.tà raw</th></tr></thead><tbody>{verification.bim_element_samples.map((e,i) => <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1.5 font-mono">{e.element_id || e.unique_id || "—"}</td><td className="px-2 py-1.5">{e.category || "—"}</td><td className="px-2 py-1.5">{[e.family,e.type].filter(Boolean).join(" / ") || "—"}</td><td className="px-2 py-1.5">{e.level || "—"}</td><td className="px-2 py-1.5">{e.source_filename || "—"}{e.source_sheet ? ` · ${e.source_sheet}` : ""}{e.source_row ? ` · riga ${e.source_row}` : ""}</td><td className="px-2 py-1.5 text-right font-mono">{verification.quantity_field === "area" ? e.area ?? "—" : verification.quantity_field === "volume" ? e.volume ?? "—" : verification.quantity_field === "length" ? e.length ?? "—" : "—"}</td></tr>)}</tbody></table></div>}</td></tr>}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 p-5">
          <div className="ml-auto max-w-xs space-y-1.5 text-sm">
            <Row label="Importo lavori" value={euro(totals.subtotal)} />
            {totals.ribasso_pct > 0 && <Row label={`Ribasso d'asta ${totals.ribasso_pct}%`} value={`- ${euro(totals.ribasso_amount)}`} />}
            {totals.ribasso_pct > 0 && <Row label="Importo ribassato" value={euro(totals.discounted)} />}
            <Row label="Oneri sicurezza" value={euro(totals.security_charges)} />
            <div className="flex justify-between pt-2 border-t border-orange-200 text-orange-600 font-display font-bold text-base">
              <span>TOTALE OFFERTA</span><span className="font-mono" data-testid="cme-total-amount">{euro(totals.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {picker && <PrezzarioPicker offerId={offerId} region={offer?.region || ""} offerProvince={offer?.province || ""} existingItems={items} onClose={() => setPicker(false)} onAdded={load} />}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-slate-600"><span>{label}</span><span className="font-mono text-slate-900">{value}</span></div>
);